/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { InjectionToken } from "@angular/core";
import { SysqueSizeRangeItemsVM } from "libs/sysque/shared/ui/src/lib/models/sysque-size-range-vm";
import { FieldFabConduitSizeRangeVM, HandAndMachineBendSizeRangeVM, IFamilyInformationItem, KickHandAndMachineBendSizeRangeVM, Parameters } from "libs/sysque/toolbelt/feature/src/lib/components/add-family-information/add-family-information-types";
import { AddFamilyInformationData, AddFamilyInformationReturnCodes, FamilyInformationItem, ServiceType, connectorDescription } from "libs/sysque/toolbelt/feature/src/lib/models/family-information-model";
import { Observable } from "rxjs";
import { LineGraphicsDialogConfig } from '../../../../../feature/src/lib/components/add-family-information/line-graphics';
import { CreateServiceTypeDialogConfig } from "libs/sysque/toolbelt/feature/src/lib/components/add-family-information/create-service-type/types";
import { DeviceEquipmentDetailsDataToApply, DeviceEquipmentDetailsInputData } from "libs/sysque/toolbelt/feature/src/lib/models/device-equipment-details-return-codes";
import { SysqueElectricalState } from 'libs/sysque/toolbelt/feature/src/lib/models/sysque-electrical-state';

export interface IAddFamilyInformationFacade {
  registerControllers(): void;
  setItemToAdd(item: AddFamilyInformationData): IFamilyInformationItem;
  convertCableTrayStringToSysqueSizeRangeVM(data: string): SysqueSizeRangeItemsVM[];
  convertCableTraySysqueSizeRangeVMToString(data: SysqueSizeRangeItemsVM[]): string;
  convertConduitStringToSysqueSizeRangeVM(data: string): SysqueSizeRangeItemsVM[];
  convertConduitSysqueSizeRangeVMToString(data: SysqueSizeRangeItemsVM[]): string;
  convertFieldFabStringToSysqueSizeRangeVM(data: FieldFabConduitSizeRangeVM): SysqueSizeRangeItemsVM[];
  convertSysqueSizeRangeItemsVMToFieldFab(data: SysqueSizeRangeItemsVM[]): FieldFabConduitSizeRangeVM;
  convertHandAndMachineBendStringToSysqueSizeRangeVM(data: HandAndMachineBendSizeRangeVM, offsetType: string): SysqueSizeRangeItemsVM[];
  convertSysqueSizeRangeVMToHandAndMachineBendString(data: SysqueSizeRangeItemsVM[], offsetType: string): HandAndMachineBendSizeRangeVM;
  convertKickHandAndMachineBendStringToSysqueSizeRangeVM(data: KickHandAndMachineBendSizeRangeVM): SysqueSizeRangeItemsVM[];
  convertSysqueSizeRangeItemsVMToKickHandAndMachineBendString(data: SysqueSizeRangeItemsVM[]): KickHandAndMachineBendSizeRangeVM;
  convertFeetToMm(feet: number): number;
  convertMmToFeet(mm: number): number;
  getFamilyInformationItem(): IFamilyInformationItem;
  getFamilyInformationResponse(): FamilyInformationItem;
  updateItem(item: IFamilyInformationItem): void;
  getCableTrayConnectors(): connectorDescription[];
  getConduitConnectors(): connectorDescription[];
  getJunctionBoxConnectors(): connectorDescription[];
  getUserCustomContentConnectors(): connectorDescription[];
  getDeviceEquipmentConnectors(): connectorDescription[];
  convertIntARGBToRGBHex(argb: number): string;
  convertRGBHexToIntARGB(hex: string): number | null;
  convertRGBAStringToIntARGB(color: string): number;
  saveFamilyInformationInXml(item: IFamilyInformationItem): void;
  windowResize(height: number): void;
  sendClosePopupAction(): void;
  getUpdatedProjectParameters(): Parameters;
  onClickedToOpenProjectData(): void;
  updateLocalStorage(key: string, value: any): void;
  getPdfLocationPath(): Observable<string>;
  openPdfFile(path: string): Observable<AddFamilyInformationReturnCodes>;
  getPatterns(): Observable<string[]>;
  applyChangesForLineGraphics(item: IFamilyInformationItem, lineGraphicsDialogConfig: LineGraphicsDialogConfig): IFamilyInformationItem;
  saveServiceType(serviceType: ServiceType): void;
  applyCreateServiceTypeChanges(item: IFamilyInformationItem, createServiceTypeDialogConfig: CreateServiceTypeDialogConfig): ServiceType;
  deleteServiceType(serviceType: ServiceType): void;
  applyDeleteServiceTypeChanges(item: IFamilyInformationItem): boolean;
  getUserCustomImage(systemName: string, name: string): Observable<string>;
  validateAndGetData(): Observable<DeviceEquipmentDetailsInputData>;
  applyDeviceEquipmentDetails(item: DeviceEquipmentDetailsDataToApply): void;
  getLocalStorageData(key: string): IFamilyInformationItem[] | SysqueElectricalState;
  getHangerSetUpStatus(): boolean;
}

export const ADD_FAMILY_INFORMATION_FACADE_TOKEN = new InjectionToken<IAddFamilyInformationFacade>(
  'ADD_FAMILY_INFORMATION_FACADE_TOKEN'
);